import {
  useCreateRoleMutation,
  useGetPrivilegesQuery,
  useGetRoleDetailsQuery,
  useUpdateRolesPermissionMutation,
} from '@/store/api/rolesApi';
import { showToastMessage } from '@/utils';
import { createRoleSchema } from '@/utils/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import z from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { UpdatedAccess } from '@/types/rolesTypes';
type roleFormType = z.infer<ReturnType<typeof createRoleSchema>>;

// Temporarily hidden from Create/Edit Roles. Remove entries from this list to restore sections.
const hiddenRoleSections = new Set([
  'Agents',
  'Sales',
  'Lottery',
  'Transaction',
  'Withdraw Request',
  'KYC',
  'Claim Request',
  'BetControl',
  'CoinManagement',
  'General Settings',
  'Commission',
]);

const useCreateRole = () => {
  // Form setup
  const { id, type } = useParams();
  const isViewMode = type === 'view';
  const isEditMode = type === 'edit';
  const navigate = useNavigate();

  // get roles data ************************************
  const queryParams = useMemo(
    () => ({
      page: 1,
      limit: 50,
      // search: debouncedSearch.trim(),
    }),
    []
  );
  const { data, isLoading: privilegesLoading } = useGetPrivilegesQuery(queryParams);
  const rolesData = useMemo(
    () => data?.data?.filter((module) => !hiddenRoleSections.has(module.permissions)),
    [data?.data]
  );
  const totalItems = data?.pagination.totalItems || 0;
  const { data: viewRoleData, isLoading: viewLoading } = useGetRoleDetailsQuery(Number(id), {
    skip: !id,
  });

  const [createRole, { isLoading }] = useCreateRoleMutation();
  const [updateRolesPermission, { isLoading: editLoading }] = useUpdateRolesPermissionMutation();
  const onSubmit = async (data: roleFormType) => {
    try {
      const payload = {
        name: data.name,
        description: data.description,
        privilegesWithAccess: data.privilegesWithAccess.map((item) => ({
          privilegeId: item.privilegeId,
          access: item.access,
        })),
      };

      if (type === 'edit') {
        await updateRolesPermission({
          id: Number(id),
          ...payload,
        }).unwrap();

        showToastMessage('success', 'Role updated successfully!');
      } else {
        await createRole(payload).unwrap();

        showToastMessage('success', 'Role created successfully!');
      }

      reset();
      navigate('/roles-and-privileges');
    } catch (err: any) {
      const msg = err?.data?.message || 'Something went wrong';
      showToastMessage('error', msg);
    }
  };
  const { control, handleSubmit, setValue, watch, reset } = useForm<roleFormType>({
    resolver: zodResolver(createRoleSchema()),
    defaultValues: {
      enableAll: false,
      name: '',
      description: '',
      privilegesWithAccess: [],
    },
    mode: 'onChange',
  });
  const formValue = watch();
  useEffect(() => {
    if (rolesData?.length) {
      const formatted = rolesData.map((module) => ({
        privilegeId: module.id,
        hasAccess: false,
        description: module.description,
        access: Object.keys(module.access || {}).reduce(
          (acc, key) => {
            acc[key] = false;
            return acc;
          },
          {} as Record<string, boolean>
        ),
      }));

      reset({
        privilegesWithAccess: formatted,
      });
    }
  }, [rolesData, reset]);

  const watchPrivileges = useWatch({
    control,
    name: 'privilegesWithAccess',
  });

  const isFirstRender = useRef(true);
  const isResetting = useRef(false);

  // 1️⃣ toggle effect
  useEffect(() => {
    // skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // skip if reset() just fired
    if (isResetting.current) {
      isResetting.current = false;
      return;
    }

    watchPrivileges?.forEach((module, moduleIndex) => {
      if (!module?.access) return;

      Object.entries(module.access).forEach(([permissionKey, value]) => {
        if (value !== module.hasAccess) {
          setValue(
            `privilegesWithAccess.${moduleIndex}.access.${permissionKey}`,
            module.hasAccess,
            { shouldDirty: false }
          );
        }
      });
    });
  }, [JSON.stringify(watchPrivileges?.map((m) => m?.hasAccess))]);

  // 2️⃣ reset/load effect
  useEffect(() => {
    if (viewRoleData?.rolePrivileges?.length && rolesData?.length) {
      const roleInfo = viewRoleData.rolePrivileges[0]?.role;
      const formatted = rolesData.map((module) => {
        const existing = viewRoleData.rolePrivileges.find((item) => item.privilegeId === module.id);

        const accessSource = existing?.privilege?.access ?? module.access ?? {};
        const accessKeys = Object.keys(accessSource);

        const access = accessKeys.reduce<Record<keyof UpdatedAccess, boolean>>(
          (acc, key) => {
            const typedKey = key as keyof UpdatedAccess;
            acc[typedKey] = existing?.updatedAccess?.[typedKey] ?? false;
            return acc;
          },
          {} as Record<keyof UpdatedAccess, boolean>
        );

        const hasAccess = Object.values(access).some(Boolean);

        return {
          privilegeId: module.id,
          description: module.description,
          hasAccess,
          access,
        };
      });

      // ✅ flag BEFORE reset so toggle effect sees it
      isResetting.current = true;

      reset({
        name: roleInfo?.name ?? '',
        description: roleInfo?.description ?? '',
        privilegesWithAccess: formatted,
      });
    }
  }, [viewRoleData, rolesData, reset]);

  // const watchPrivileges = watch('privilegesWithAccess');
  const handleUniversalToggle = (checked: boolean) => {
    rolesData?.forEach((module, moduleIndex) => {
      const accessKeys = Object.keys(module.access || {});

      // set module access
      setValue(`privilegesWithAccess.${moduleIndex}.hasAccess`, checked);

      // set all permissions
      accessKeys.forEach((permissionKey) => {
        setValue(`privilegesWithAccess.${moduleIndex}.access.${permissionKey}`, checked);
      });
    });
  };

  return {
    control,
    handleSubmit,
    onSubmit,
    rolesData,
    totalItems,
    privilegesLoading,
    isLoading,
    isViewMode,
    isEditMode,
    editLoading,
    watch,
    handleUniversalToggle,
  };
};

export default useCreateRole;
